// ============================================================
// share — "Share to Mesa Robles": building and reading a recipe package
// ============================================================
// Two blocks go out in every package:
//
//   display     what Mesa Robles renders. Finished text in Mesa Robles' own
//               terms, because the site never runs calculator math.
//   calculator  LinksLab's own four blocks, for the round trip back. Mesa
//               Robles stores them unchanged and never reads them.
//
// No formula lives here. Every number comes from the app's own modules, so a
// shared recipe cannot disagree with what the cook saw on screen. The shape is
// checked by the vendored validator, which is the same file all three sites
// use — see src/vendor/mesarobles/package.js.
//
// Prices are never exported. They are the cook's own, they live in
// sc_ingredient_db per device, and the recipient's are the right ones for
// their costs.
import { SECTIONS, CASING_DB, DB } from '../data/ingredients';
import { normalizeSections, DEFAULT_SECTIONS } from '../data/methods';
import {
  toGrams, pctToGrams, calcTotals, calcNutrition, calcCasing, calcPPM,
  formulaTotalPct, isCasingRow, rowFeet, genId,
} from './calculations';
import { slugify, packageFilename, validatePackage } from '../vendor/mesarobles/package';

export const CALCULATOR_FORMAT_VERSION = 1;
export const APP_URL  = 'https://linkslab.mesarobles.com';
export const HELP_URL = `${APP_URL}/#share-help`;
export const NITRITE_LIMIT_PPM = 156;
export const NITRITE_TOLERANCE_PPM = 0.5;
export const NITRITE_BASIS = 'ingoing nitrite in the meat mix (sausage)';
// Mesa Robles placement this app suggests. The cook chooses on import.
export const SUGGESTED_DEPARTMENT = 'smoke';
export const SUGGESTED_SECTION    = 'curing-sausage';

// The commit, injected at build time (see netlify.toml). Null in dev.
const BUILD = process.env.REACT_APP_BUILD_COMMIT || null;

// ── Display formatting ──────────────────────────────────────────────────────
// Deliberately the same arithmetic as the reference fixtures, down to the
// ounce constant, so an export can be diffed against them.
const OZ = 28.349523125;

const lbOz = (g) => {
  const oz = Math.round((g / OZ) * 10) / 10;
  if (oz < 16) return `${oz} oz`;
  const lb = Math.floor(oz / 16);
  return `${lb} lb ${Math.round((oz - lb * 16) * 10) / 10} oz`;
};

// Whole grams from 100 g up, one decimal below it.
const gramsText = (g) => (g >= 100
  ? Math.round(g).toLocaleString('en-US')
  : String(Math.round(g * 10) / 10));

const wt = (g) => `${gramsText(g)} g (${lbOz(g)})`;

// Percentages and amounts are already rounded by the calculator; String()
// keeps 70 as "70" rather than "70.000".
const num = (n) => String(n);

// ── Normalizing a stored recipe ─────────────────────────────────────────────
// The app stores text-input values as strings. A package stores numbers, so
// every one of them goes through here: a blank becomes null, never 0.
const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const round = (n, dp) => {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
};

const macroObject = (macro) => macro ? {
  protein:  macro[0],
  carbs:    macro[1],
  fat:      macro[2],
  sodiumMg: macro[3],
  calories: macro[4],
} : null;

// True when the steps are still, step for step, the shipped defaults. It
// cannot tell "never edited" from "edited back to identical", and need not.
const isDefaultMethod = (method) => {
  if (!Array.isArray(method) || method.length !== DEFAULT_SECTIONS.length) return false;
  return method.every((sec, i) => {
    const def = DEFAULT_SECTIONS[i];
    return sec.key === def.key && sec.title === def.title
      && sec.steps.length === def.steps.length
      && sec.steps.every((text, j) => text === def.steps[j]);
  });
};

/** A stored recipe (any schema LinksLab has ever written) → the `recipe` block.
 *  Every compatibility rule lives here, in one place. The two places
 *  loadRecipe used to apply `||` to a number are deliberately not copied: a
 *  stored 0 is a real value. */
export function normalizeRecipe(raw) {
  const unitSystem = raw.unitSystem === 'Imperial (lbs)' ? 'Imperial (lbs)' : 'Metric (g)';
  const rows       = (raw.rows || []).filter(r => r && (r.name || r.pct || rowFeet(r) > 0));

  const casingName = raw.selectedCasing !== null && raw.selectedCasing !== undefined
    ? (CASING_DB[raw.selectedCasing]?.[0] ?? null)
    : null;

  const method = normalizeSections(raw.methodSections).map(sec => ({
    key:   sec.key,
    title: sec.title,
    color: sec.color,
    steps: sec.steps.map(s => s.text),
  }));

  return {
    name:        raw.recipeName || 'Untitled Recipe',
    style:       raw.sausageStyle || null,
    cookMethod:  raw.cookMethod || 'Hot Smoked',
    notes:       raw.recipeNotes || '',
    // '' and 0 both mean "no rating"; 1-5 is a rating.
    rating:      numOrNull(raw.rating) || null,
    savedAt:     raw.savedAt || null,
    sourceRecipeId: raw.id || null,     // advisory only; the reader mints its own

    basis:  { weight: numOrNull(raw.meatWeight) ?? 1000, unit: unitSystem },
    losses: {
      equipmentG: numOrNull(raw.equipLoss) ?? 150,
      cookPct:    numOrNull(raw.cookLossPct) ?? 25,
    },
    target: {
      cooked: numOrNull(raw.targetCooked),
      unit:   raw.targetUnit === 'Metric (g)' ? 'Metric (g)' : 'Imperial (lbs)',
      locked: Boolean(raw.targetLocked),
    },

    // Inputs only. The resolved ft/lb and everything computed from it are in
    // `derived`; the casing's own spec is in `references`.
    casingPlan: casingName === null ? null : {
      name:         casingName,
      shippedIndex: raw.selectedCasing,   // advisory — resolve by name first
      linkLengthIn: numOrNull(raw.linkLength) ?? 6,
      linkWeightG:  numOrNull(raw.linkWeight),
      userFtPerLb:  numOrNull(raw.userFtPerLb),
    },
    typicalLinkWeightG: numOrNull(raw.typicalLinkWeightG) ?? 120,

    // Row ids are regenerated on every load, so they carry no meaning across a
    // share. pct and feet are mutually exclusive.
    ingredients: rows.map(r => isCasingRow(r)
      ? { section: 'casing', name: r.name, feet: numOrNull(r.feet), note: r.note || '' }
      : { section: r.sectionKey, name: r.name, pct: numOrNull(r.pct), note: r.note || '' }),

    // The tolerant shape normalizeSections() already accepts: steps as plain
    // strings, no editor ids. Newlines inside a step are kept.
    method,
    methodIsDefault: isDefaultMethod(method),
  };
}

/** The facts from the sender's databases the recipe leaned on. Without these,
 *  nutrition and the range badges change meaning on another device. */
export function buildReferences(recipe, ingDB) {
  const seen = new Set();
  const ingredients = [];

  recipe.ingredients.forEach(row => {
    const key = `${row.section}\u0000${row.name}`;
    if (seen.has(key)) return;
    seen.add(key);

    const live = ingDB ? ingDB.getIngredient(row.section, row.name) : null;
    // getStatus() in the grid reads the shipped table, not the live one, so
    // that is the range the cook was actually judged against.
    const shipped = (DB[row.section] || []).find(e => e[0] === row.name) || null;
    const range = shipped
      ? { typMin: shipped[1], typMax: shipped[2], hardMax: shipped[3] }
      : (live ? { typMin: live.typMin, typMax: live.typMax, hardMax: live.hardMax } : null);

    ingredients.push({
      section:  row.section,
      name:     row.name,
      known:    Boolean(live),
      macroPer100g: row.section === 'casing' ? null : macroObject(live?.macro ?? null),
      range,
      costUnit: live?.costUnit ?? null,   // the unit, never the price
      note:     live?.note || '',
    });
  });

  const plan = recipe.casingPlan;
  const idx  = plan ? CASING_DB.findIndex(c => c[0] === plan.name) : -1;
  const spec = idx !== -1 ? CASING_DB[idx] : null;
  const liveCasing = plan && ingDB ? ingDB.getIngredient('casing', plan.name) : null;

  const cureFactors = {};
  recipe.ingredients
    .filter(r => r.section === 'cure' && r.name)
    .forEach(r => {
      // Record the factor actually applied, including the 625 fallback, so a
      // custom cure priced as Cure #1 is visible rather than implied.
      const factorPerPct = calcPPM(1, r.name);
      cureFactors[r.name] = {
        factorPerPct,
        matchedKnownCure: knownCure(r.name),
      };
    });

  return {
    ingredients,
    casing: spec ? {
      name:       spec[0],
      diameterMm: spec[1],
      ftPerLb:    spec[2],
      prepNote:   spec[3],
      source:     spec[4],
      senderFtPerLb: liveCasing?.typMin ?? null,
    } : null,
    cureFactors,
    nitriteLimitPpm: NITRITE_LIMIT_PPM,
  };
}

// A cure the app has a real conversion factor for. Anything else falls back to
// the Cure #1 factor, which is a guess worth warning about.
const KNOWN_CURES = [
  'Prague Powder #1 (Cure #1)',
  'Cultured Celery Juice Powder',
  'EcoCure #1',
];
const knownCure = (name) => KNOWN_CURES.includes(name);

/** Display-only snapshot: the numbers the sender saw. A reader that edits
 *  anything must recompute; this is carried so the recipe can be shown. */
export function buildDerived(recipe, ingDB) {
  const basisG = toGrams(recipe.basis.weight, recipe.basis.unit);
  const rows   = toCalcRows(recipe);

  const filled      = rows.filter(r => !isCasingRow(r) && r.name && r.pct);
  const casingLines = rows.filter(r => isCasingRow(r) && r.name && rowFeet(r) > 0);

  const totals    = calcTotals([...filled, ...casingLines], basisG,
                               recipe.losses.equipmentG, recipe.losses.cookPct);
  const nutrition = calcNutrition(filled, basisG, ingDB);
  const casing    = buildDerivedCasing(recipe, totals, ingDB);

  return {
    computedAt: new Date().toISOString(),
    basisGrams: basisG,
    formulaTotalPct: round(formulaTotalPct(filled), 3),

    // Per-row weights, so a reader can print an ingredient table without
    // doing any arithmetic. Casing rows carry feet instead of a weight.
    ingredientWeights: recipe.ingredients.map(r => r.section === 'casing'
      ? { name: r.name, section: r.section, feet: r.feet ?? null }
      : {
          name:    r.name,
          section: r.section,
          grams:   basisG > 0 ? round(pctToGrams(r.pct || 0, basisG), 1) : null,
          oz:      basisG > 0 ? round(pctToGrams(r.pct || 0, basisG) / 28.3495, 2) : null,
        }),

    totals: {
      formulaG:    totals.totalG,
      formulaLb:   totals.totalLb,
      stuffableG:  totals.stuffable,
      stuffableLb: totals.stuffLb,
      cookLossPct: totals.cookLossPct,
      cookedG:     totals.cookedG,
      cookedLb:    totals.cookedLb,
      cookedRange: {
        atThirtyPctLossG:  totals.cookedLoG,
        atTwentyPctLossG:  totals.cookedHiG,
        atThirtyPctLossLb: totals.cookedLoLb,
        atTwentyPctLossLb: totals.cookedHiLb,
      },
      casingFeet:   totals.casingFeet,
      meatFatPct:   totals.mfPct,
      meatFatG:     totals.mfG,
      additionsPct: totals.additionsPct,
      additionsG:   totals.additionsG,
      liquidPct:    totals.liquidPct,
      binderPct:    totals.binderPct,
      spicePct:     totals.spicePct,
    },

    nutritionPer100g: {
      basis:    'pre-cook, per 100 g of mix',
      protein:  nutrition.protein,
      carbs:    nutrition.carbs,
      fat:      nutrition.fat,
      sodiumMg: nutrition.sodium,
      calories: nutrition.calories,
      fatRatioTotalPct:   nutrition.fatRatioTotal,
      fatRatioMeatFatPct: nutrition.fatRatioMFOnly,
      // Carried on purpose: a 60%-covered estimate must not look like a full one.
      coveragePct: nutrition.coverage ?? 0,
      unknownPct:  nutrition.unknownPct ?? 0,
      unknownIngredients: nutrition.unknownNames ?? [],
    },

    casing,
    cure: buildCureFigures(recipe, basisG),
  };
}

// The recipe block, back in the row shape the calculator's own functions take.
function toCalcRows(recipe) {
  return recipe.ingredients.map(r => r.section === 'casing'
    ? { sectionKey: 'casing', name: r.name, pct: '', feet: String(r.feet ?? '') }
    : { sectionKey: r.section, name: r.name, pct: String(r.pct ?? '') });
}

function buildDerivedCasing(recipe, totals, ingDB) {
  const plan = recipe.casingPlan;
  if (!plan) return null;
  const idx  = CASING_DB.findIndex(c => c[0] === plan.name);
  const spec = idx !== -1 ? CASING_DB[idx] : null;
  if (!spec) return null;

  const live     = ingDB ? ingDB.getIngredient('casing', plan.name) : null;
  const override = plan.userFtPerLb > 0 ? plan.userFtPerLb : (live?.typMin || null);
  const cr = calcCasing(spec, totals.stuffable, plan.linkLengthIn,
                        plan.linkWeightG, override, recipe.losses.cookPct);
  if (!cr) return null;

  return {
    ftPerLbUsed: cr.ftPerLb,
    // 'senderIngredientDb' only when the sender's value actually differs from
    // the shipped table — otherwise it is the shipped value.
    ftPerLbSource: plan.userFtPerLb > 0 ? 'userOverride'
                   : (live?.typMin && live.typMin !== spec[2] ? 'senderIngredientDb' : 'shippedTable'),
    feetNeeded:   cr.feetNeeded,
    metersNeeded: cr.metersNeeded,
    feetWith15PctBuffer:   cr.feetBuffer,
    metersWith15PctBuffer: cr.metersBuffer,
    predictedLinkWeightG:  cr.predictedLinkWeightG,
    predictedLinkWeightOz: cr.predictedLinkWeightOz,
    effectiveLinkWeightG:  cr.effectiveLinkWt,
    linkWeightSource: plan.linkWeightG > 0 ? 'userMeasured' : 'predicted',
    estimatedLinks:   cr.estLinks,
    cookedLinkG:      cr.cookedLinkG,
    cookedLinkOz:     cr.cookedLinkOz,
    prepNote:         cr.prepNote,
  };
}

// Nitrite, clamped. calcPPM returns null for anything it cannot use, so a bad
// amount is reported as a bad amount rather than as a reassuring figure.
function buildCureFigures(recipe, basisG) {
  return recipe.ingredients
    .filter(r => r.section === 'cure' && r.name)
    .map(r => {
      const ppm = calcPPM(r.pct, r.name);
      const factorPerPct = calcPPM(1, r.name);
      const invalid = ppm === null;
      return {
        name: r.name,
        pct:  invalid ? 0 : r.pct,
        ...(invalid ? { invalidInputPct: r.pct } : {}),
        // The exact product, not the 1 dp badge figure.
        ppm: invalid ? null : round(r.pct * factorPerPct, 4),
        factorPerPct,
        limitPpm: NITRITE_LIMIT_PPM,
        // The app flags above 156.5, so a 0.25% Cure #1 batch (156.25 ppm) is
        // not reported over the limit by a rounding artefact.
        tolerancePpm: NITRITE_TOLERANCE_PPM,
        withinLimit: invalid ? null
          : r.pct * factorPerPct <= NITRITE_LIMIT_PPM + NITRITE_TOLERANCE_PPM,
        grams: basisG > 0 && !invalid ? round(pctToGrams(r.pct, basisG), 2) : null,
      };
    });
}

// ── display: what Mesa Robles renders ───────────────────────────────────────

function buildFacts(recipe, derived) {
  const facts = [];
  const { totals, basisGrams, casing, nutritionPer100g } = derived;

  facts.push({ label: 'Meat + fat basis', value: wt(basisGrams) });
  facts.push({
    label: 'Total mix',
    value: `${wt(totals.formulaG)} · ${num(derived.formulaTotalPct)}% of basis`,
  });
  facts.push({
    label: 'Stuffable',
    value: `${wt(totals.stuffableG)} after ${gramsText(recipe.losses.equipmentG)} g equipment loss`,
  });
  facts.push({
    label: 'Expected cooked',
    value: `${wt(totals.cookedG)} at ${num(totals.cookLossPct)}% loss `
         + `(${gramsText(totals.cookedRange.atThirtyPctLossG)}–`
         + `${gramsText(totals.cookedRange.atTwentyPctLossG)} g)`,
  });

  if (casing && recipe.casingPlan) {
    const links = casing.estimatedLinks > 0
      ? ` · about ${casing.estimatedLinks} links of ${num(recipe.casingPlan.linkLengthIn)} in`
      : '';
    facts.push({
      label: 'Casing',
      value: `${recipe.casingPlan.name} · ${num(casing.feetNeeded)} ft needed, `
           + `buy ${num(casing.feetWith15PctBuffer)} ft${links}`,
    });
  }

  const n = nutritionPer100g;
  if (basisGrams > 0 && n.calories > 0) {
    const unknown = n.coveragePct < 100 && n.unknownIngredients.length
      ? ` · no macro data for ${n.unknownIngredients.join(', ')}`
      : '';
    facts.push({
      label: 'Nutrition per 100 g (pre-cook)',
      value: `${Math.round(n.calories)} kcal · protein ${num(n.protein)} g · `
           + `fat ${num(n.fat)} g · carbs ${num(n.carbs)} g · sodium ${num(n.sodiumMg)} mg · `
           + `${num(n.coveragePct)}% of the mix covered${unknown}`,
    });
  }

  if (recipe.target.locked && recipe.target.cooked > 0) {
    facts.push({
      label: 'Target finished weight',
      value: `${wt(toGrams(recipe.target.cooked, recipe.target.unit))} — the basis is solved from this`,
    });
  }

  return facts;
}

function buildIngredientGroups(recipe, derived) {
  const byName = new Map(derived.ingredientWeights.map(w => [`${w.section}\u0000${w.name}`, w]));
  const groups = [];

  SECTIONS.forEach(sect => {
    const rows = recipe.ingredients.filter(r => r.section === sect.key && r.name);
    if (rows.length === 0) return;

    const items = rows.map(row => {
      if (sect.key === 'casing') {
        const spec = CASING_DB.find(c => c[0] === row.name) || null;
        const prep = spec ? spec[3] : '';
        const note = [prep, row.note].filter(Boolean).join(' · ') || null;
        return {
          name: row.name,
          quantity: row.feet > 0 ? `${num(row.feet)} ft` : null,
          grams: null,
          note,
        };
      }
      const w = byName.get(`${row.section}\u0000${row.name}`);
      const raw = w && w.grams !== null && w.grams !== undefined ? w.grams : null;
      // A negative amount is not a weight. It still travels — nitrite never
      // blocks an export — but the page shows the entry and asks for a check
      // rather than printing "-10 g", which the validator rightly refuses.
      const usable = raw !== null && raw >= 0 && row.pct !== null && row.pct >= 0;
      return {
        name: row.name,
        // Row notes travel at full length — the PDF's 45-character cut is a
        // page-layout limit, not a property of the recipe.
        quantity: usable
          ? `${wt(raw)} · ${num(row.pct)}%`
          : (row.pct !== null ? `${num(row.pct)}% — check this amount` : null),
        grams: usable ? raw : null,
        note: row.note || null,
      };
    });

    groups.push({ group: sect.label, items });
  });

  return groups;
}

function buildSafety(recipe, derived) {
  const cureRows  = derived.cure;
  const coldSmoked = recipe.cookMethod === 'Cold Smoked';
  if (cureRows.length === 0 && !coldSmoked) return null;

  const warnings = [];

  const cure = cureRows.map(c => ({
    name: c.name,
    amountText: c.grams !== null
      ? `${gramsText(c.grams)} g · ${num(c.pct)}%`
      : null,
    grams: c.grams,
    // LinksLab always shows the amount it used; nothing is hidden.
    withheld: false,
  }));

  cureRows.forEach(c => {
    if (c.ppm === null) {
      warnings.push(`${c.name}: the cure amount could not be read, so no nitrite figure was calculated. Check it in LinksLab.`);
    }
    if (!knownCure(c.name)) {
      warnings.push(`${c.name} is not one of the cures LinksLab knows, so its nitrite was estimated with the Cure #1 factor (625 ppm per 1%). Check the supplier's spec.`);
    }
  });

  if (coldSmoked && cureRows.length === 0) {
    // The app's own warning, verbatim.
    warnings.push('⚠️ Cure #1 required');
  }

  let nitrite = null;
  if (cureRows.length > 0) {
    const anyInvalid = cureRows.some(c => c.ppm === null);
    const total = cureRows.reduce((sum, c) => sum + (c.ppm ?? 0), 0);
    const over  = total > NITRITE_LIMIT_PPM + NITRITE_TOLERANCE_PPM;
    nitrite = anyInvalid ? {
      ppm: null,
      text: 'could not be calculated: check the cure amount',
      level: 'CAUTION',
      limitPpm: NITRITE_LIMIT_PPM,
      tolerancePpm: NITRITE_TOLERANCE_PPM,
      basis: NITRITE_BASIS,
      note: 'LinksLab could not read one of the cure amounts. Open the recipe in LinksLab and check it.',
    } : {
      ppm: round(total, 4),
      text: `${round(total, 1).toFixed(1)} ppm`,
      level: over ? 'OVER' : 'OK',
      limitPpm: NITRITE_LIMIT_PPM,
      tolerancePpm: NITRITE_TOLERANCE_PPM,
      basis: NITRITE_BASIS,
      note: `LinksLab flags anything above ${NITRITE_LIMIT_PPM + NITRITE_TOLERANCE_PPM} ppm.`,
    };
  }

  const disclaimer = [
    cureRows.length > 0
      ? 'Cure #1 is toxic in large amounts. Weigh it on a 0.1 g scale and verify cure calculations independently.'
      : 'Verify cure calculations independently.',
    coldSmoked ? 'Cold-smoked sausage must contain Cure #1.' : '',
  ].filter(Boolean).join(' ');

  return { cure, nitrite, disclaimer, warnings };
}

function buildDisplay(recipe, derived) {
  const subtitle = [recipe.style, recipe.cookMethod].filter(Boolean).join(' · ') || null;
  const casing   = derived.casing;

  return {
    title: recipe.name,
    subtitle,
    summary: recipe.notes || null,
    suggested: {
      department: SUGGESTED_DEPARTMENT,
      section:    SUGGESTED_SECTION,
      slug:       slugify(recipe.name),
    },
    elevationFt: null,
    facts: buildFacts(recipe, derived),
    timing: {
      // The one timing field this app can state. The cook adds the rest on
      // import, where they know their own schedule.
      serves: casing && casing.estimatedLinks > 0 && recipe.casingPlan
        ? `About ${casing.estimatedLinks} links (${num(recipe.casingPlan.linkLengthIn)} in)`
        : null,
      hands_on_time: null,
      total_time:    null,
      salt_time:     null,
      keeps:         null,
      heat_level:    null,
    },
    ingredientGroups: buildIngredientGroups(recipe, derived),
    methodSections: recipe.method
      .map(sec => ({
        phase: sec.title,
        steps: sec.steps.filter(s => typeof s === 'string' && s.trim() !== ''),
      }))
      .filter(sec => sec.steps.length > 0),
    notes: [],
    safety: buildSafety(recipe, derived),
    allergens: null,
    rescaleNote: 'Open in LinksLab to scale this formula to your batch size.',
  };
}

// ── The package ─────────────────────────────────────────────────────────────

/** A stored recipe → a Mesa Robles recipe package, ready to validate. */
export function buildSharePackage(rawRecipe, { ingDB, build = BUILD } = {}) {
  const recipe  = normalizeRecipe(rawRecipe);
  const derived = buildDerived(recipe, ingDB);

  return {
    format: 'mesarobles-recipe-package',
    version: 1,
    kind: 'recipe',
    exportedAt: new Date().toISOString(),
    source: { app: 'LinksLab', appUrl: APP_URL, build },
    display: buildDisplay(recipe, derived),
    calculator: {
      app: 'LinksLab',
      formatVersion: CALCULATOR_FORMAT_VERSION,
      data: {
        recipe,
        references: buildReferences(recipe, ingDB),
        derived,
        excluded: {
          ingredientPrices: "sender-specific; cost must be computed from the reader's own prices",
          pricingSettings:  'operation-level (labor, overhead, margin, tax), not a property of this recipe',
          rowIds:           'regenerated on every load; carry no meaning across devices',
        },
      },
    },
  };
}

/** validatePackage returns a discriminated union — { ok: true, pkg, warnings }
 *  or { ok: false, errors, warnings } — so every caller would otherwise have
 *  to remember that `errors` is absent on success. One shape instead. */
export function validateSharePackage(input) {
  const r = validatePackage(input);
  return {
    ok:       r.ok === true,
    pkg:      r.ok === true ? r.pkg : null,
    errors:   r.errors   || [],
    warnings: r.warnings || [],
  };
}

/** The filename a share downloads as. */
export function shareFilename(pkg) {
  return packageFilename(pkg?.display?.suggested?.slug || pkg?.display?.title || 'recipe');
}

// ── Reading a package back ──────────────────────────────────────────────────

/** A package → an sc_recipes record. The casing is resolved by name, so a
 *  reordered shipped table cannot silently change which casing it means. */
export function toLinksLabRecipe(pkg, { nameTaken } = {}) {
  const r = pkg.calculator.data.recipe;
  const byName = CASING_DB.findIndex(c => c[0] === r.casingPlan?.name);
  const selectedCasing = r.casingPlan
    ? (byName !== -1 ? byName
       : (Number.isInteger(r.casingPlan.shippedIndex) ? r.casingPlan.shippedIndex : null))
    : null;

  let name = r.name;
  if (typeof nameTaken === 'function' && nameTaken(name)) name = `${name} (shared)`;

  return {
    id: genId(),                               // never reuse sourceRecipeId
    savedAt: new Date().toISOString(),
    recipeName: name,
    cookMethod: r.cookMethod,
    unitSystem: r.basis.unit,
    meatWeight: r.basis.weight,
    sausageStyle: r.style || '',
    rating: r.rating ?? '',
    recipeNotes: r.notes || '',
    equipLoss: r.losses.equipmentG,
    cookLossPct: r.losses.cookPct,
    typicalLinkWeightG: r.typicalLinkWeightG,
    targetCooked: r.target.cooked ?? '',
    targetUnit: r.target.unit,
    targetLocked: r.target.locked,
    selectedCasing,
    linkLength: r.casingPlan?.linkLengthIn ?? 6,
    linkWeight: r.casingPlan?.linkWeightG ?? '',
    userFtPerLb: r.casingPlan?.userFtPerLb ?? '',
    rows: r.ingredients.map(row => row.section === 'casing'
      ? { id: genId(), sectionKey: 'casing', name: row.name, pct: '',
          note: row.note || '', feet: row.feet == null ? '' : String(row.feet) }
      : { id: genId(), sectionKey: row.section, name: row.name,
          pct: row.pct == null ? '' : String(row.pct), note: row.note || '' }),
    methodSections: normalizeSections(r.method),
  };
}

/** True when a parsed file looks like a LinksLab backup rather than a share. */
export function looksLikeBackup(parsed) {
  return Boolean(parsed && typeof parsed === 'object'
    && (parsed.format === 'linkslab-backup' || (parsed.app === 'pastramilab' && parsed.data)));
}
